import React from "react";
import { Link } from "react-router-dom";
import {
  FileText,
  Shield,
  Key,
  CheckCircle,
  ArrowRight,
  Zap,
  Globe,
} from "lucide-react";

function Home() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="relative z-10 pb-8 bg-white sm:pb-16 md:pb-20 lg:w-full lg:pb-28 xl:pb-32">
            <main className="mt-10 mx-auto max-w-7xl px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
              <div className="sm:text-center lg:text-left">
                <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
                  <span className="block xl:inline">Secure Digital</span>{" "}
                  <span className="block text-primary-600 xl:inline">
                    Document Signing
                  </span>
                </h1>
                <p className="mt-3 text-base text-gray-500 sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-5 md:text-xl lg:mx-0">
                  Sign, verify, and manage your digital documents with
                  military-grade security. Our platform ensures document
                  integrity and prevents tampering through advanced
                  cryptographic signatures.
                </p>
                <div className="mt-5 sm:mt-8 sm:flex sm:justify-center lg:justify-start">
                  <div className="rounded-md shadow">
                    <Link
                      to="/register"
                      className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 md:py-4 md:text-lg md:px-10"
                    >
                      Get started
                    </Link>
                  </div>
                  <div className="mt-3 sm:mt-0 sm:ml-3">
                    <Link
                      to="/login"
                      className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-primary-700 bg-primary-100 hover:bg-primary-200 md:py-4 md:text-lg md:px-10"
                    >
                      Sign in
                    </Link>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
        <div className="lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
          <div className="h-56 w-full bg-gradient-to-br from-primary-400 to-primary-600 sm:h-72 md:h-96 lg:w-full lg:h-full flex items-center justify-center">
            <FileText className="h-32 w-32 text-white opacity-80" />
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-primary-600 font-semibold tracking-wide uppercase">
              Features
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Everything you need for secure document signing
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Our platform provides enterprise-grade security with user-friendly
              interface
            </p>
          </div>

          <div className="mt-10">
            <div className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-10">
              {[
                {
                  name: "Cryptographic Security",
                  description:
                    "Military-grade encryption ensures your documents cannot be forged or tampered with.",
                  icon: Shield,
                },
                {
                  name: "Digital Signatures",
                  description:
                    "Create unique digital signatures that are mathematically linked to your documents.",
                  icon: Key,
                },
                {
                  name: "Instant Verification",
                  description:
                    "Verify document authenticity and integrity in seconds with our verification system.",
                  icon: Zap,
                },
                {
                  name: "Global Accessibility",
                  description:
                    "Access your signed documents from anywhere in the world with secure cloud storage.",
                  icon: Globe,
                },
              ].map((feature) => (
                <div key={feature.name} className="relative">
                  <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-primary-500 text-white">
                    <feature.icon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <p className="ml-16 text-lg leading-6 font-medium text-gray-900">
                    {feature.name}
                  </p>
                  <dd className="mt-2 ml-16 text-base text-gray-500">
                    {feature.description}
                  </dd>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="py-16 bg-gray-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 space-y-8 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-base text-primary-600 font-semibold tracking-wide uppercase">
              How it works
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Simple steps to secure document signing
            </p>
          </div>

          <div className="relative">
            <div className="relative max-w-5xl mx-auto">
              <div className="flex flex-col md:flex-row items-center justify-between space-y-8 md:space-y-0 md:space-x-8">
                {[
                  {
                    step: 1,
                    title: "Upload Document",
                    description:
                      "Upload your PDF, DOC, or other document files to our secure platform.",
                    icon: FileText,
                  },
                  {
                    step: 2,
                    title: "Generate Signature",
                    description:
                      "Our system creates a unique cryptographic signature for your document.",
                    icon: Key,
                  },
                  {
                    step: 3,
                    title: "Verify & Share",
                    description:
                      "Verify authenticity anytime and share with confidence knowing it's secure.",
                    icon: CheckCircle,
                  },
                ].map((item, index) => (
                  <div
                    key={item.step}
                    className="flex flex-col items-center text-center max-w-xs"
                  >
                    <div className="flex items-center justify-center h-16 w-16 rounded-full bg-primary-100 text-primary-600">
                      <item.icon className="h-8 w-8" />
                    </div>
                    <div className="mt-4">
                      <h3 className="text-lg font-medium text-gray-900">
                        {item.step}. {item.title}
                      </h3>
                      <p className="mt-2 text-sm text-gray-500">
                        {item.description}
                      </p>
                    </div>
                    {index < 2 && (
                      <ArrowRight
                        className="hidden md:block absolute top-8 h-6 w-6 text-gray-400"
                        style={{ left: `${33.33 * (index + 1) - 3}%` }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-primary-700">
        <div className="max-w-2xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
            <span className="block">Ready to secure your documents?</span>
            <span className="block">Start signing today.</span>
          </h2>
          <p className="mt-4 text-lg leading-6 text-primary-200">
            Join thousands of users who trust our platform for their document
            signing needs.
          </p>
          <Link
            to="/register"
            className="mt-8 w-full inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-primary-600 bg-white hover:bg-primary-50 sm:w-auto"
          >
            Get started for free
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center space-x-6">
            <Link to="/login" className="text-gray-500 hover:text-gray-900">
              Sign In
            </Link>
            <Link to="/register" className="text-gray-500 hover:text-gray-900">
              Register
            </Link>
          </div>
          <div className="mt-8">
            <p className="text-center text-base text-gray-400">
              &copy; 2025 Digital Signature Platform. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;
